import config from 'config';
import * as repo from 'db/repositories';

/**
 * Couche SSE Mercure unique PWA / Capacitor.
 * Mercure = accélérateur ; la vérité reste SyncManager (?since=).
 */
var TOPIC_PUBLIC = 'https://mares/public';

var RealtimeService = {

	_source: null,
	_wanted: false,
	_bound: false,
	_connecting: false,
	_buffer: [],
	_token: null,
	_tokenExpiresAt: 0,
	_hubUrl: null,
	_topics: [ TOPIC_PUBLIC ],
	_resumeAfterSync: false,

	/**
	 * Démarre la connexion si Mercure est configuré et le réseau dispo.
	 */
	start: function()
	{
		this._wanted = true;
		this._bindAppEvents();

		if( !app.is( 'online' ) || typeof EventSource === 'undefined' )
		{
			return;
		}

		this._ensureConnection();
	},

	_bindAppEvents: function()
	{
		if( this._bound )
		{
			return;
		}

		this._bound = true;

		// Vide le buffer après une sync HTTP concurrente
		app.on( 'synched', _.bind( function()
		{
			if( this._buffer.length && !app.is( 'synching' ) && !app.is( 'working' ) && !this._resumeAfterSync )
			{
				this._flushBuffer();
			}
		}, this ) );
	},

	stop: function()
	{
		this._wanted = false;
		this._resumeAfterSync = false;
		this._closeSource();
	},

	/**
	 * Ferme SSE sans abandonner l'intention de rester connecté (background / hidden).
	 */
	pause: function()
	{
		this._closeSource();
	},

	reconnect: function()
	{
		this._closeSource();
		if( this._wanted && app.is( 'online' ) )
		{
			this._ensureConnection();
		}
	},

	/**
	 * Réveil (foreground / visible) : sync HTTP puis SSE.
	 */
	resume: function()
	{
		var self = this;
		this._closeSource();
		this._buffer = [];

		if( !this._wanted || !app.is( 'online' ) )
		{
			return $.Deferred().resolve();
		}

		this._resumeAfterSync = true;

		return app.requestSync( { reason: 'realtime-resume' } ).always( function()
		{
			self._resumeAfterSync = false;
			self._flushBuffer();
			if( self._wanted && app.is( 'online' ) )
			{
				self._ensureConnection();
			}
		} );
	},

	/**
	 * À appeler quand auth change (nouveau JWT subscriber).
	 */
	onAuthChanged: function()
	{
		this._token = null;
		this._tokenExpiresAt = 0;
		if( this._wanted )
		{
			this.reconnect();
		}
	},

	_closeSource: function()
	{
		if( this._source )
		{
			try
			{
				this._source.close();
			}
			catch( e ) {}
			this._source = null;
		}
		this._connecting = false;
	},

	_hubConfigured: function()
	{
		var fromConfig = config.mercure && config.mercure.url;
		var fromData = app.getStore( 'data' ) && app.getStore( 'data' ).get( 'mercure' );
		var url = fromConfig || ( fromData && fromData.url ) || null;
		return !!url;
	},

	_resolveHubUrl: function()
	{
		if( config.mercure && config.mercure.url )
		{
			return config.mercure.url;
		}

		var data = app.getStore( 'data' );
		var mercure = data && data.get( 'mercure' );
		return ( mercure && mercure.url ) || null;
	},

	_ensureConnection: function()
	{
		var self = this;

		if( !this._wanted || this._connecting || this._source )
		{
			return;
		}

		if( !this._hubConfigured() )
		{
			return;
		}

		this._connecting = true;

		this._fetchSubscriberToken().done( function()
		{
			self._openEventSource();
		} ).fail( function()
		{
			self._connecting = false;
			console.warn( '[Realtime] token Mercure indisponible' );
		} );
	},

	_fetchSubscriberToken: function()
	{
		var deferred = $.Deferred();
		var self = this;
		var now = Date.now();

		// Réutilise le JWT s'il reste > 60 s
		if( this._token && this._tokenExpiresAt > now + 60000 )
		{
			deferred.resolve();
			return deferred.promise();
		}

		var url = ( config.url && config.url.mercure_token )
			? config.url.mercure_token
			: ( config.host.base + '/default/mercure.json' );

		$.ajax( {
			url: url,
			method: 'GET',
			dataType: 'json',
		} ).done( function( json )
		{
			if( !json || !json.token || !json.url )
			{
				deferred.reject();
				return;
			}

			self._token = json.token;
			self._hubUrl = json.url;
			self._topics = json.topics && json.topics.length ? json.topics : [ TOPIC_PUBLIC ];
			self._tokenExpiresAt = now + ( ( json.expires_in || 3600 ) * 1000 );
			deferred.resolve();
		} ).fail( function()
		{
			deferred.reject();
		} );

		return deferred.promise();
	},

	_openEventSource: function()
	{
		var self = this;
		var hub = this._hubUrl || this._resolveHubUrl();

		if( !hub || !this._token )
		{
			this._connecting = false;
			return;
		}

		var params = new URLSearchParams();
		_.each( this._topics, function( topic )
		{
			params.append( 'topic', topic );
		} );
		params.set( 'authorization', this._token );

		var url = hub + ( hub.indexOf( '?' ) >= 0 ? '&' : '?' ) + params.toString();

		try
		{
			this._source = new EventSource( url );
		}
		catch( e )
		{
			this._connecting = false;
			console.warn( '[Realtime] EventSource failed', e );
			return;
		}

		this._source.onopen = function()
		{
			self._connecting = false;
			console.log( '[Realtime] connected' );
		};

		this._source.onerror = function()
		{
			self._connecting = false;
			// EventSource reconnecte seul ; après expiration JWT on force un refresh
			if( self._tokenExpiresAt && Date.now() > self._tokenExpiresAt )
			{
				self._token = null;
				self._closeSource();
				if( self._wanted && app.is( 'online' ) )
				{
					_.delay( function() { self._ensureConnection(); }, 2000 );
				}
			}
		};

		this._source.onmessage = function( evt )
		{
			self._onMessage( evt );
		};
	},

	_onMessage: function( evt )
	{
		var data;
		try
		{
			data = JSON.parse( evt.data );
		}
		catch( e )
		{
			console.warn( '[Realtime] JSON invalide', evt.data );
			return;
		}

		if( !data || !data.entity || !data.uuid )
		{
			return;
		}

		console.log( '[Realtime] event', data.type || data.entity, data.uuid, data );

		// Pendant sync HTTP : buffer pour éviter les courses
		if( app.is( 'synching' ) || app.is( 'working' ) || this._resumeAfterSync )
		{
			console.log( '[Realtime] buffered (sync en cours)', data.uuid );
			this._buffer.push( data );
			return;
		}

		this._applyEvent( data );
	},

	_flushBuffer: function()
	{
		var self = this;
		var list = this._buffer.slice();
		this._buffer = [];

		_.each( list, function( data )
		{
			self._applyEvent( data );
		} );
	},

	_applyEvent: function( data )
	{
		var self = this;
		var table = {
			pond: 'ponds',
			visit: 'visits',
			image: 'images',
		}[ data.entity ];

		if( !table )
		{
			return;
		}

		repo.getByUuid( table, data.uuid ).then( function( local )
		{
			// Pas en cache : pas de GET systématique ; la carte rafraîchit son viewport
			if( !local )
			{
				if( data.entity === 'pond' )
				{
					app.trigger( 'realtime:entity', data );
				}
				return;
			}

			// Brouillon local : ne pas écraser
			if( local.todo )
			{
				return;
			}

			// Déjà à jour (idempotence etag)
			if( data.etag && local.etag === data.etag )
			{
				return;
			}

			if( data.deleted )
			{
				return repo.applyRemoteRow( table, {
					uuid: data.uuid,
					etag: data.etag,
					updated_at: data.updated_at,
					deleted_at: data.updated_at || Date.now(),
				} ).then( function( changed )
				{
					if( changed )
					{
						app.trigger( 'realtime:entity', data );
						app.trigger( 'synched' );
					}
				} );
			}

			return self._fetchEntity( data.entity, data.uuid ).then( function( attrs )
			{
				if( !attrs )
				{
					// 404 → traiter comme suppression
					return repo.applyRemoteRow( table, {
						uuid: data.uuid,
						deleted_at: data.updated_at || Date.now(),
					} );
				}

				return repo.applyRemoteRow( table, attrs );
			} ).then( function( changed )
			{
				if( changed )
				{
					app.trigger( 'realtime:entity', data );
					app.trigger( 'synched' );
				}
			} );
		} ).catch( function( err )
		{
			console.warn( '[Realtime] apply failed', err );
		} );
	},

	_fetchEntity: function( entity, uuid )
	{
		var base = {
			pond: config.url.pond,
			visit: config.url.visit,
			image: config.url.image,
		}[ entity ];

		var deferred = $.Deferred();

		if( !base )
		{
			deferred.resolve( null );
			return deferred.promise();
		}

		$.ajax( {
			url: base + '/' + encodeURIComponent( uuid ),
			method: 'GET',
			dataType: 'json',
		} ).done( function( json )
		{
			deferred.resolve( json || null );
		} ).fail( function( xhr )
		{
			if( xhr && ( xhr.status === 404 || xhr.status === 204 ) )
			{
				deferred.resolve( null );
				return;
			}
			deferred.resolve( null );
		} );

		return deferred.promise();
	},
};

export default RealtimeService;
